import { FC, useCallback, useContext, useEffect, useMemo, useState } from "react";
import "./TaskDeadlines.css";
import { TaskContext } from "../../../../contexts/TaskContext";
import { dateToNiceString } from "../../../../helpers/dateToNiceString";
import { Slider } from "primereact/slider";
import Button from "../../../UiLib/Button/Button";
import { AppContext } from "../../../../App";
import WidgetContainer from "../../../UiLib/WidgetContainer/WidgetContainer";
import { TIME_VARIANTS } from "../../TaskAddEditForm/FormConstants/FormConstants";

import { ReactComponent as BluePen } from "../../../../assets/editButtonGray.svg";
import { ReactComponent as Cross } from "../../../../assets/images/cross.svg";
import useOutsideAlerter from "../../../../hooks/useOutsideAlert";
import { FloatLabel } from "primereact/floatlabel";
import { Calendar } from "primereact/calendar";
import { DoubleKeyMap } from "../../../../helpers/DoubleKeyMap";

const END_STATUS = "Завершено";

const TaskDeadlines: FC = () => {
    const taskContext = useContext(TaskContext);
    const { showToast } = useContext(AppContext);

    const [activeWidget, setActiveWidget] = useState<boolean>(false);
    const [requiredTime, setRequiredTime] = useState<number>(0);
    const [customHours, setCustomHours] = useState<number>(0);
    const [customMinutes, setCustomMinutes] = useState<number>(0);

    const outsideAlerterRefPriority = useOutsideAlerter<HTMLDivElement>(() => setActiveWidget((prev) => false));

    useEffect(() => {
        setRequiredTime(taskContext.requiredTime ?? 0);

        if (taskContext.taskInformation?.allottedTime !== undefined) {
            const totalMinutes = taskContext.taskInformation.allottedTime;
            const hours = Math.floor(totalMinutes / 60);
            const minutes = totalMinutes % 60;
            setCustomHours(hours);
            setCustomMinutes(minutes);
        }
    }, [taskContext.taskInformation]);

    const getDeadlineColor = (dateEnd: string | undefined): string => {
        if (taskContext.taskInformation?.status.some((xx) => xx.current && xx.name === END_STATUS)) {
            return "";
        }

        if (!dateEnd) return "var(--base-branded-gray-800)";

        try {
            const deadline = new Date(dateEnd);
            const now = new Date();
            const timeDiff = deadline.getTime() - now.getTime();
            const daysDiff = timeDiff / (1000 * 3600 * 24);

            if (daysDiff > 3) {
                return "#52c41a";
            } else if (daysDiff >= 1) {
                return "#faad14";
            } else if (daysDiff >= 0) {
                return "#ff4d4f";
            } else {
                return "#f5222d";
            }
        } catch (error) {
            console.error("Error parsing date:", error);
            return "var(--base-branded-gray-800)";
        }
    };

    const formatMinutesToTime = useMemo(() => {
        let minutes = taskContext.requiredTime;

        if (minutes === undefined || minutes === null || isNaN(minutes) || minutes < 0) {
            return undefined;
        }

        const hours = Math.floor(minutes / 60);
        const mins = minutes % 60;

        return {
            formattedHours: hours,
            formattedMinutes: mins,
        };
    }, [taskContext.requiredTime]);

    const getTime = useCallback(() => {
        if (formatMinutesToTime) {
            return `${formatMinutesToTime.formattedHours
                .toString()
                .padStart(2, "0")}:${formatMinutesToTime.formattedMinutes.toString().padStart(2, "0")}`;
        } else {
            return "-";
        }
    }, [formatMinutesToTime]);

    const handleSliderChange = useCallback((e: { value: number | number[] }) => {
        const value = Array.isArray(e.value) ? e.value[0] : e.value;
        taskContext.setPercentageCompletion(value);
    }, []);

    const handleSliderChangeEnd = useCallback(
        async (e: { value: number | number[] }) => {
            const value = Array.isArray(e.value) ? e.value[0] : e.value;
            taskContext.setPercentageCompletion(value);
            await taskContext.updatePercentageCompletion(value);
        },
        [taskContext.fastTaskUpdate]
    );

    // const handleCustomTimeChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    //     let value = e.target.value.replace(/\D/g, '');
    //     if (value.length === 0) {
    //         value = "0";
    //     }

    //     const id = e.target.id;

    //     switch (id) {
    //         case 'custom-hours-input':
    //             setCustomHours(parseInt(value.slice(0, 4)));
    //             setRequiredTime((Number(value.slice(0, 4)) * 60) + Number(customMinutes));
    //             break;
    //         case 'custom-minutes-input':
    //             setCustomMinutes(parseInt(value.slice(0, 2)));
    //             setRequiredTime((Number(customHours) * 60) + Number(value.slice(0, 2)));
    //             break;
    //     }
    // }, [customMinutes, customHours]);

    const handleTimeVariantClick = useCallback(
        async (hours: number) => {
            const minutes = hours * 60;
            taskContext.setRequiredTime(minutes);

            try {
                await taskContext.updateRequiredTime(minutes);
            } catch (error) {
                showToast("Ошибка обновления сроков задачи");
            }
        },
        [taskContext.updateRequiredTime]
    );

    // const handleSaveTime = useCallback(async () => {
    //     const hours = customHours || 0;
    //     const minutes = customMinutes || 0;
    //     const totalMinutes = hours * 60 + minutes;

    //     taskContext.setRequiredTime(totalMinutes);
    //     setActiveWidget(false);

    //     try {
    //         await taskContext.updateRequiredTime(totalMinutes);
    //     } catch (error) {
    //         showToast("Ошибка обновления сроков задачи")
    //     }
    // }, [
    //     taskContext.updateRequiredTime,
    //     customHours,
    //     customMinutes
    // ]);

    const renderTimeVariantButton = useCallback(
        (item: number) => {
            const currentHours = Math.round((taskContext.requiredTime ?? 0) / 60);
            const isSelected = item === currentHours;
            const buttonClass = isSelected ? "task-deadlines-time-chose" : "task-deadlines-time-unchose";
            return (
                <button onClick={() => handleTimeVariantClick(item)} className={buttonClass} key={item} type="button">
                    {item}
                </button>
            );
        },
        [handleTimeVariantClick, taskContext.requiredTime]
    );

    const formatDateSafely = (dateString: string | undefined, format: string): string => {
        if (!dateString) return "-";

        try {
            const date = new Date(dateString);
            if (isNaN(date.getTime())) {
                return "-";
            }
            return dateToNiceString(date, format);
        } catch (error) {
            console.error("Error formatting date:", error);
            return "-";
        }
    };

    const updateTime = async (type: "start" | "end", date: Date) => {
        if (
            (type === "start" && taskContext.dateEnd && date.getTime() > taskContext.dateEnd.getTime()) ||
            (type === "end" && taskContext.dateStart && taskContext.dateStart.getTime() > date.getTime())
        ) {
            showToast(`Дата начала не может быть после даты окончания`);
            return;
        }
        if (type === "start") {
            taskContext.setDateStart(date);
        } else {
            taskContext.setDateEnd(date);
        }
        await taskContext.updateDate(type, date);
    };

    return (
        <div className="task-deadlines-component">
            <div className="task-deadlines-head">
                <h2>Сроки задачи</h2>
                <div className="task-deadlines-point">
                    <div
                        style={{
                            backgroundColor: getDeadlineColor(taskContext.taskInformation?.dateEnd),
                        }}
                        className="task-deadlines-point-status"
                    ></div>
                </div>
                <Button
                    id={taskContext.editTask && !taskContext.forceLinkTerm ? "button-pen" : "button-pen-gray"}
                    callBack={
                        taskContext.editTask
                            ? !taskContext.forceLinkTerm
                                ? () => setActiveWidget(true)
                                : () => showToast("Сроки задачи синхронизированы с внешней системой")
                            : () => showToast("У вас нет прав на редактирование задачи")
                    }
                    image={<BluePen />}
                />
            </div>
            {activeWidget && (
                <div ref={outsideAlerterRefPriority} className="task-deadlines-modal">
                    <WidgetContainer>
                        <div className="task-deadlines-modal-head">
                            <a>Сроки задачи</a>
                            <Button id="button-cross" callBack={() => setActiveWidget(false)} image={<Cross />} />
                        </div>
                        <div className="task-deadlines-modal-body">
                            <div className="task-deadlines-modal-body-block">
                                <div className="task-deadlines-line"></div>
                                <div className="task-deadlines-calendar-body">
                                    <div className="task-deadlines-calendar">
                                        <FloatLabel>
                                            <Calendar
                                                dateFormat="dd.mm.yy"
                                                onChange={(value) => updateTime("start", value.value as Date)}
                                                value={taskContext.dateStart}
                                                inputId="start-date-input"
                                            />
                                            <label htmlFor="start-date-input">Дата начала</label>
                                        </FloatLabel>
                                    </div>
                                    <div className="task-deadlines-calendar">
                                        <FloatLabel>
                                            <Calendar
                                                dateFormat="dd.mm.yy"
                                                onChange={(value) => updateTime("end", value.value as Date)}
                                                value={taskContext.dateEnd}
                                                inputId="end-date-input"
                                            />
                                            <label htmlFor="end-date-input">Дата окончания</label>
                                        </FloatLabel>
                                    </div>
                                </div>
                            </div>
                            <div className="task-deadlines-modal-body-block">
                                <h2>Срок выполнения в часах</h2>
                                <div className="task-deadlines-time-variants">
                                    {TIME_VARIANTS.map(renderTimeVariantButton)}
                                </div>
                            </div>
                            {/* <div className="task-deadlines-modal-body-block">
                                <h2>Свое время</h2>
                                <div className="task-deadlines-time-body">
                                    <div className="task-deadlines-time-custom">
                                        <div className="task-deadlines-time-custom-input">
                                            <input
                                                pattern="\d*"
                                                value={customHours}
                                                onChange={handleCustomTimeChange}
                                                type="text"
                                                id="custom-hours-input"
                                                inputMode="numeric"
                                            />
                                            <div className="task-deadlines-time-custom-text">часы</div>
                                        </div>
                                        <div>
                                            <div className="task-deadlines-time-custom-pass">:</div>
                                        </div>
                                        <div className="task-deadlines-time-custom-input">
                                            <input
                                                pattern="\d*"
                                                value={customMinutes}
                                                onChange={handleCustomTimeChange}
                                                type="text"
                                                id="custom-minutes-input"
                                                className="custom-time-input"
                                                inputMode="numeric"
                                            />
                                            <div className="task-deadlines-time-custom-text">минуты</div>
                                        </div>
                                    </div>
                                    {taskContext.requiredTime !== requiredTime &&
                                        <Button
                                            text="Сохранить"
                                            callBack={handleSaveTime}
                                            className="task-deadlines-modal-actions"
                                        />
                                    }
                                </div>
                            </div> */}
                        </div>
                    </WidgetContainer>
                </div>
            )}
            <div className="task-deadlines-body">
                <div className="task-deadlines-body-row">
                    <a className="task-deadlines-body-row-head">Дата начала:</a>
                    <a className="task-deadlines-body-row-value">
                        {formatDateSafely(taskContext.dateStart?.toString(), "dd.MM.yy")}
                    </a>
                </div>
                <div className="task-deadlines-body-row">
                    <a className="task-deadlines-body-row-head">Дата окончания:</a>
                    <a
                        style={{
                            color: getDeadlineColor(taskContext.taskInformation?.dateEnd),
                        }}
                        className="task-deadlines-body-row-value"
                    >
                        {formatDateSafely(taskContext.dateEnd?.toString(), "dd.MM.yy")}
                    </a>
                </div>
                <div className="task-deadlines-editable-row-wrapper">
                    <div className="task-deadlines-body-row">
                        <a className="task-deadlines-body-row-head">Плановые трудозатраты:</a>
                        <a className="task-deadlines-body-row-value">{getTime()}</a>
                    </div>
                </div>
                <div className="task-deadlines-body-row">
                    <a className="task-deadlines-body-row-head">Прогресс:</a>
                    <a className="task-deadlines-body-row-value">{taskContext.percentageCompletion}%</a>
                </div>
                <div className="task-deadlines-body-row">
                    <Slider
                        className="task-completion-percentage-slider"
                        style={{ height: "7px", borderRadius: "3px", width: "100%", margin: "0px 9px" }}
                        step={1}
                        value={taskContext.percentageCompletion}
                        onChange={handleSliderChange}
                        onSlideEnd={handleSliderChangeEnd}
                    />
                </div>
            </div>
        </div>
    );
};

export default TaskDeadlines;
